import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    createShippingNote,
    deleteShippingNote,
    fetchShippingInstructions,
    updateShippingNote,
} from '../../context/CommonContex';

/**
 * Shipping instructions for the customers on screen, pinned on top of each row's
 * sticky notes. Two sources:
 *
 *   - "cetec":  synced from Cetec into customer_addresses by the 5 AM job.
 *               Read-only here; one per customer address. An order shows only
 *               the address whose id equals orders.shipto_id ("This order
 *               ships here"). Other addresses for the same customer stay hidden.
 *   - "manual": added by users from the sticky notes board, stored in
 *               customer_shipping_notes. Editable and deletable; never touched
 *               by the Cetec sync. These stay per customer and show on every
 *               order for that customer.
 *
 * Key: customer id (orders.customer_id, or a quote's customer_id). The customer
 * list is loaded once; each order filters it with its own shipto_id. A quote
 * uses the same customer id, then keeps only the ship-to instruction: the
 * address whose id matches a quote line's shipto_id, or, when that id is
 * missing, the customer's ship-to addresses. Bill-to and other addresses stay hidden.
 */

const BATCH_SIZE = 100;

/** Permanent Cetec instructions are always blue, so they stay distinct from
 *  green shipping comments and yellow extra comments. */

const customerIdOf = (order) => {
    const id = order?.customer?.id ?? order?.customer_id ?? order?.customerId;
    return id == null || id === '' ? null : String(id);
};

/** Ship-to address ids on a quote. The quote header has the customer id;
 *  each line carries shipto_id. Zero means the line has no ship-to. */
const shipToIdsOfQuote = (quote) => {
    const ids = new Set();
    (quote?.lines || []).forEach((line) => {
        const id = line?.shipto_id;
        if (id == null || id === '' || Number(id) === 0) return;
        ids.add(String(id));
    });
    return Array.from(ids);
};

const currentUser = () => (
    localStorage.getItem('userEmail')
    || localStorage.getItem('email')
    || localStorage.getItem('userName')
    || ''
);

/** Server items arrive without `source` from older backends; those are Cetec. */
const isManual = (item) => item?.source === 'manual';

const isShipToAddress = (item) => !isManual(item) && String(item?.addressType || '').toLowerCase() === 'shipto';

export default function useShippingInstructions(orders) {
    const [byCustomer, setByCustomer] = useState({});
    const cacheRef = useRef({});
    const loadedRef = useRef(new Set());
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const commit = useCallback((next) => {
        cacheRef.current = next;
        if (mountedRef.current) setByCustomer(next);
    }, []);

    // Stable signature, so a big grid re-rendering does not refetch.
    const signature = useMemo(
        () => Array.from(new Set((orders || []).map(customerIdOf).filter(Boolean))).sort().join('|'),
        [orders]
    );

    useEffect(() => {
        const wanted = signature ? signature.split('|') : [];
        const missing = wanted.filter((id) => !loadedRef.current.has(id));
        if (!missing.length) return;
        missing.forEach((id) => loadedRef.current.add(id));

        (async () => {
            try {
                const merged = {};
                for (let i = 0; i < missing.length; i += BATCH_SIZE) {
                    Object.assign(merged, await fetchShippingInstructions(missing.slice(i, i + BATCH_SIZE)));
                }
                commit({ ...cacheRef.current, ...merged });
            } catch (err) {
                // Allow a retry on the next render; the column still works without them.
                missing.forEach((id) => loadedRef.current.delete(id));
                console.warn('Shipping instructions could not be loaded', err?.message || err);
            }
        })();
    }, [signature, commit]);

    const toStickyNotes = (customerId, items) => (items || []).map((item) => {
        if (isManual(item)) {
            return {
                id: `ship-${item.noteId}`,
                noteId: item.noteId,
                customerId,
                source: 'manual',
                text: item.instructions,
                color: item.color || 'blue',
                createdBy: item.createdBy,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                pinned: true,
                locked: false,
            };
        }
        return {
            id: `cetec-${item.addressId}`,
            addressId: item.addressId,
            customerId,
            source: 'cetec',
            addressType: item.addressType,
            name: item.name,
            text: item.instructions,
            isShipTo: true,
            color: 'orange',
            pinned: true,
            locked: true,
        };
    });

    /** addressIds null shows every instruction for the customer. A Set shows
     *  manuals plus the Cetec rows whose address is in that set. */
    const selectFor = useCallback((record, addressIds) => {
        const customerId = customerIdOf(record);
        const list = byCustomer[customerId] || [];
        const picked = addressIds
            ? list.filter((item) => isManual(item) || addressIds.has(String(item.addressId)))
            : list;
        return toStickyNotes(customerId, picked);
    }, [byCustomer]);

    /** Shipping instructions for one order: the Cetec ship-to for this order, then user-added ones. */
    const forOrder = useCallback(
        (order) => {
            const shipTo = order?.shipto_id == null || order.shipto_id === '' ? null : String(order.shipto_id);
            return selectFor(order, new Set(shipTo == null ? [] : [shipTo]));
        },
        [selectFor]
    );

    /** Ship-to instruction for one quote. The line's shipto_id wins when it
     *  matches an address. Otherwise only addressType "shipto" is shown.
     *  Bill-to and other addresses stay off the quote. */
    const forQuote = useCallback((quote) => {
        const customerId = customerIdOf(quote);
        const list = byCustomer[customerId] || [];
        const shipToIds = new Set(shipToIdsOfQuote(quote));
        const matchedById = shipToIds.size
            ? list.filter((item) => isManual(item) || shipToIds.has(String(item.addressId)))
            : [];
        const hasCetecMatch = matchedById.some((item) => !isManual(item));
        const picked = hasCetecMatch
            ? matchedById
            : list.filter((item) => isManual(item) || isShipToAddress(item));
        return toStickyNotes(customerId, picked);
    }, [byCustomer]);

    const replaceFor = useCallback((customerId, updater) => {
        const key = String(customerId);
        commit({ ...cacheRef.current, [key]: updater(cacheRef.current[key] || []) });
    }, [commit]);

    /** Adds an instruction for the whole customer; it then shows on all their orders. */
    const add = useCallback(async (customerId, { text, color }) => {
        if (!customerId) throw new Error('This order has no customer, so a shipping instruction cannot be added.');
        const saved = await createShippingNote(customerId, { text, color, createdBy: currentUser() });
        replaceFor(customerId, (list) => [...list, saved]);
        return { id: `ship-${saved.noteId}` };
    }, [replaceFor]);

    const update = useCallback(async (note, { text, color }) => {
        const saved = await updateShippingNote(note.noteId, { text, color, updatedBy: currentUser() });
        replaceFor(note.customerId, (list) => list.map((item) => (
            isManual(item) && String(item.noteId) === String(note.noteId) ? saved : item
        )));
        return saved;
    }, [replaceFor]);

    const remove = useCallback(async (note) => {
        await deleteShippingNote(note.noteId);
        replaceFor(note.customerId, (list) => list.filter((item) => (
            !(isManual(item) && String(item.noteId) === String(note.noteId))
        )));
    }, [replaceFor]);

    return { forOrder, forQuote, customerIdFor: customerIdOf, add, update, remove };
}
